import { Info, IterationCwIcon, RefreshCwIcon } from "lucide-react";
import React, { useMemo } from "react";

import LoopConfig from "./LoopConfig";
import { LoopConfiguration, LoopType } from "./types";

import InputVariablesField from "@/components/PowerBlock/Block/InputVariablesField";
import SectionGroup from "@/components/PowerBlock/Block/SectionGroup";
import { SourceType } from "@/components/PowerBlock/Block/SourcesField/SelectSource";
import ValidatedField from "@/components/PowerBlock/Block/ValidatedField";
import { Input } from "@/components/ui/input";
import { BasicTooltip } from "@/components/ui/Tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface ConfigurationViewProps {
  loopType: LoopType;
  endConditionJsonPath?: string;
  maxIterations?: number;
  inputVariables: string[];
  sources: Record<SourceType, string[]>;
  inputVariablesIsLoading: boolean;
  readonly?: boolean;
  onLoopTypeChange: (type: LoopType) => void;
  onEndConditionChange: (jsonPath?: string) => void;
  onMaxIterationsChange?: (maxIterations: number | undefined) => void;
  onAutofill: (mappings: Record<string, string>) => void;
  promptConfig?: LoopConfiguration["prompt_config"];
  onPromptConfigChange: (config: LoopConfiguration["prompt_config"]) => void;
  showVerboseMode?: boolean;
  workflowConfig?: LoopConfiguration["workflow_config"];
  onWorkflowConfigChange: (config: LoopConfiguration["workflow_config"]) => void;
}

const ConfigurationView = React.memo(
  ({
    loopType,
    endConditionJsonPath,
    maxIterations,
    inputVariables,
    sources,
    inputVariablesIsLoading,
    readonly = false,
    onLoopTypeChange,
    onEndConditionChange,
    onMaxIterationsChange,
    onAutofill,
    promptConfig,
    onPromptConfigChange,
    showVerboseMode = true,
    workflowConfig,
    onWorkflowConfigChange,
  }: ConfigurationViewProps) => {
    const LoopIcon = useMemo(() => RefreshCwIcon, []);

    const loopTypeOptions = useMemo(
      () => [
        { value: LoopType.PROMPT, label: "Prompt Template" },
        { value: LoopType.WORKFLOW, label: "Workflow" },
      ],
      [],
    );

    return (
      <div className="space-y-4">
        <SectionGroup
          title="Loop Type"
          description="Choose what to loop over"
          icon={<LoopIcon className="h-4 w-4" />}
        >
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Loop Type</label>
              <Select value={loopType} onValueChange={value => onLoopTypeChange(value as LoopType)}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select loop type" />
                </SelectTrigger>
                <SelectContent>
                  {loopTypeOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <LoopConfig
              loopType={loopType}
              promptConfig={promptConfig}
              onPromptConfigChange={onPromptConfigChange}
              workflowConfig={workflowConfig}
              onWorkflowConfigChange={onWorkflowConfigChange}
              readonly={readonly}
              showVerboseMode={showVerboseMode}
            />
          </div>
        </SectionGroup>

        <SectionGroup
          title="Loop Termination"
          description="Configure when the loop should stop"
          variant="default"
          icon={<IterationCwIcon className="h-4 w-4" />}
        >
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium">End Condition JSON Path (Optional)</label>
                <BasicTooltip content="Loop ends when this JSON path evaluates to true. When setting Return All Outputs to true, payload will include node information.">
                  <Info className="h-4 w-4 cursor-pointer text-gray-500 hover:text-gray-700" />
                </BasicTooltip>
              </div>
              <Input
                placeholder="e.g., $.should_continue"
                value={endConditionJsonPath || ""}
                onChange={e => onEndConditionChange(e.target.value || undefined)}
                disabled={readonly}
              />
            </div>
            <ValidatedField
              name="Max Iterations"
              path="configuration.max_iterations"
              helpTooltip="Maximum number of iterations to perform."
            >
              <Input
                type="number"
                min="1"
                placeholder="e.g., 10"
                value={maxIterations?.toString() || ""}
                onChange={e => {
                  const { value } = e.target;
                  onMaxIterationsChange?.(value ? parseInt(value, 10) : undefined);
                }}
                disabled={readonly}
              />
            </ValidatedField>
          </div>
        </SectionGroup>

        {(inputVariables.length > 0 || inputVariablesIsLoading) && (
          <SectionGroup
            title="Input Variables"
            description="Map loop variables to data sources"
            variant="default"
            icon={<IterationCwIcon className="h-4 w-4" />}
          >
            <InputVariablesField
              variables={inputVariables}
              sources={sources}
              sourceKeyPrefix="variable_mappings"
              isLoading={inputVariablesIsLoading}
              showAutofill
              onAutofill={onAutofill}
            />
            <div className="mt-2 text-xs text-gray-500">
              Special loop variables available:
              <ul className="mt-1 list-inside list-disc">
                <li>
                  <code>loop_index</code> - Current iteration number (starts at 0)
                </li>
                <li>
                  <code>previous_outputs</code> - Output from the previous iteration
                </li>
              </ul>
            </div>
          </SectionGroup>
        )}
      </div>
    );
  },
);

ConfigurationView.displayName = "LoopConfigurationView";

export default ConfigurationView;
